import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Optional, Silent } from "@/__main__/data-model.mjs";
import { model as TftProfileModel } from "@/data-models/tft-player.mjs";

const Queue = {
  categories: [String],
  id: String,
  name: String,
} satisfies ModelDefinition;

const Patch = {
  id: Number,
  majorVersion: Number,
  minorVersion: Number,
  region: String,
};

const Match = {
  gameLength: Number,
  gameStart: String,
  id: String,
  insertedAt: String,
  matchId: Number,
  patch: Patch,
  queue: Silent(Queue),
  updatedAt: String,
} satisfies ModelDefinition;

const PlayerMatchCompanion = Silent({
  contentId: String,
  icon: Optional(String),
  id: Number,
  insertedAt: String,
  itemId: Number,
  playerMatchId: Number,
  skinId: Number,
  species: String,
  updatedAt: String,
}) satisfies ModelDefinition;

export const Set = Silent({
  finalPatchMajor: Number,
  finalPatchMinor: Number,
  id: Number,
  name: String,
  number: Number,
  // patches: [Patch],
  startPatchMajor: Number,
  startPatchMinor: Number,
});

export const SetModel = createModel({
  data: {
    set: Set,
  },
}).transform(Set, (data) => data.data.set);

const Trait = Silent({
  id: Number,
  insertedAt: String,
  name: String,
  set: Set,
  setId: Number,
  tierTotal: Number,
  updatedAt: String,
});

const Unit = Silent({
  characterId: String,
  id: Number,
  insertedAt: String,
  name: String,
  rarity: Number,
  set: Set,
  setId: Number,
  updatedAt: String,
});

const PlayerMatchTrait = {
  id: Number,
  insertedAt: String,
  numUnits: Number,
  // playerMatch: model,
  playerMatchId: Number,
  style: Number,
  tierCurrent: Number,
  trait: Trait,
  traitId: Number,
  updatedAt: String,
} satisfies ModelDefinition;

const PlayerMatchUnit = {
  id: Number,
  insertedAt: String,
  itemNames: [String],
  playerMatchId: Number,
  tier: Number,
  unit: Unit,
  unitId: Number,
  updatedAt: String,
} satisfies ModelDefinition;

const PlayerMatchCoachingStats = {
  augmentEffectivenessRating: Number,
  augmentPairingRating: Number,
  carryChampionRating: Number,
  championAugmentRating: Number,
  compSynergyRating: Number,
  id: String,
  insertedAt: String,
  itemSuitabilityRating: Number,
  itemUtilizationRate: Number,
  rank: String,
  patchId: String,
  playerMatchId: String,
  unusedComponentCount: Number,
  updatedAt: String,
  versionNumber: String,
};

export const model = {
  augments: Silent([String]),
  gameStart: String,
  goldLeft: Number,
  id: String,
  insertedAt: String,
  lastRound: Number,
  level: Number,
  match: Silent(Match),
  matchId: Number,
  partnerGroupId: Silent(String),
  placement: Number,
  PlayerMatchCoachingStats: Silent(PlayerMatchCoachingStats),
  playerMatchCompanion: Silent(PlayerMatchCompanion),
  playerMatchTraits: Silent([PlayerMatchTrait]),
  playerMatchUnits: Silent([PlayerMatchUnit]),
  playersEliminated: Number,
  tftProfile: TftProfileModel,
  tftProfileId: Number,
  timeEliminated: Number,
  totalDamageToPlayers: Number,
  updatedAt: String,
} satisfies ModelDefinition;
export const TftMatchDataModel = createModel(model);

export const match = {
  gameLength: Number,
  gameStart: String,
  id: String,
  insertedAt: String,
  matchId: String,
  patch: Patch,
  playerMatches: [model],
  queue: Queue,
  updatedAt: String,
} satisfies ModelDefinition;

export const MatchModel = createModel({
  data: {
    match: match,
  },
}).transform(match, (data) => data.data.match);

export type TftMatchData = FromModel<typeof TftMatchDataModel>;

export default TftMatchDataModel;
